import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useEffect } from 'react';
import { authApi, features, isProduction } from '@/lib/api';

// Layouts
import AuthLayout from '@/layouts/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import AdminLayout from '@/layouts/AdminLayout';

// Auth pages
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';

// Affiliate pages
import AffiliateDashboard from '@/pages/affiliate/Dashboard';
import AffiliateLinks from '@/pages/affiliate/Links';
import AffiliateCommissions from '@/pages/affiliate/Commissions';
import AffiliatePayouts from '@/pages/affiliate/Payouts';
import AffiliateSettings from '@/pages/affiliate/Settings';

// Organization pages
import OrgDashboard from '@/pages/org/Dashboard';
import OrgAffiliates from '@/pages/org/Affiliates';
import OrgCampaigns from '@/pages/org/Campaigns';
import OrgCommissions from '@/pages/org/Commissions';
import OrgPayouts from '@/pages/org/Payouts';
import OrgSettings from '@/pages/org/Settings';
import OrgBilling from '@/pages/org/Billing';
import OrgCatalog from '@/pages/org/Catalog';
import OrgIntegration from '@/pages/org/Integration';

// Public pages
import Landing from '@/pages/public/Landing';
import ComingSoon from '@/pages/public/ComingSoon';
import Pricing from '@/pages/public/Pricing';
import Catalog from '@/pages/public/Catalog';
import CatalogListing from '@/pages/public/CatalogListing';
import AcceptInvite from '@/pages/public/AcceptInvite';
import JoinOrg from '@/pages/public/JoinOrg';
import EmbedAffiliateApplication from '@/pages/embed/AffiliateApplication';

// Legal pages
import TermsOfService from '@/pages/legal/TermsOfService';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import AcceptableUse from '@/pages/legal/AcceptableUse';

// Admin pages
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminOrganizations from '@/pages/admin/Organizations';
import AdminUsers from '@/pages/admin/Users';
import AdminSettings from '@/pages/admin/Settings';
import AdminCatalog from '@/pages/admin/Catalog';
import AdminBilling from '@/pages/admin/Billing';
import AdminStripeConnect from '@/pages/admin/StripeConnect';
import AdminPayoutApprovals from '@/pages/admin/PayoutApprovals';

// Protected route component
function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: string[];
}) {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'superadmin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'org_admin') {
      return <Navigate to="/org" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

function App() {
  const { tokens, setUser, setIsLoading, logout } = useAuthStore();

  useEffect(() => {
    const initAuth = async () => {
      if (tokens?.accessToken) {
        try {
          const response = await authApi.getMe();
          if (response.data.success) {
            setUser(response.data.data.user);
          } else {
            logout();
          }
        } catch {
          logout();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route
          path="/register"
          element={isProduction() ? <Navigate to="/coming-soon" replace /> : <Register />}
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Route>

      {/* Affiliate routes */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['affiliate']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<AffiliateDashboard />} />
        <Route path="/links" element={<AffiliateLinks />} />
        <Route path="/commissions" element={<AffiliateCommissions />} />
        <Route path="/payouts" element={<AffiliatePayouts />} />
        <Route path="/settings" element={<AffiliateSettings />} />
      </Route>

      {/* Organization admin routes */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['org_admin']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/org" element={<OrgDashboard />} />
        <Route path="/org/affiliates" element={<OrgAffiliates />} />
        <Route path="/org/campaigns" element={<OrgCampaigns />} />
        <Route path="/org/commissions" element={<OrgCommissions />} />
        <Route path="/org/payouts" element={<OrgPayouts />} />
        <Route path="/org/settings" element={<OrgSettings />} />
        <Route path="/org/billing" element={<OrgBilling />} />
        <Route path="/org/catalog" element={<OrgCatalog />} />
        <Route path="/org/integration" element={<OrgIntegration />} />
      </Route>

      {/* Super admin routes */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/organizations" element={<AdminOrganizations />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/catalog" element={<AdminCatalog />} />
        <Route path="/admin/billing" element={<AdminBilling />} />
        <Route path="/admin/stripe-connect" element={<AdminStripeConnect />} />
        <Route path="/admin/payout-approvals" element={<AdminPayoutApprovals />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
      </Route>

      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/coming-soon" element={<ComingSoon />} />
      <Route path="/pricing" element={<Pricing />} />
      {features.marketplace && (
        <>
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/catalog/:listingId" element={<CatalogListing />} />
        </>
      )}

      {/* Invite route - always available */}
      <Route path="/invite/:inviteCode" element={<AcceptInvite />} />

      {/* Join org route - for org signup URL flow */}
      <Route path="/join/:orgSlug" element={<JoinOrg />} />

      {/* Embeddable affiliate application form */}
      <Route
        path="/embed/apply"
        element={isProduction() ? <Navigate to="/coming-soon" replace /> : <EmbedAffiliateApplication />}
      />

      {/* Legal routes */}
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/acceptable-use" element={<AcceptableUse />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
